<template>
  <div class="form-wrapper">
    <form
      id="financing-form"
      class="financing-form"
      :class="{ 'financing-form--wide': wideCoverageNames }"
      @submit.prevent="$emit('submit')"
    >
      <div v-if="errorMessage" class="error-message">
        {{ errorMessage }}
      </div>
      <form-radio-button-list
        id="customerType"
        data-qa="customer-type"
        :title="retrieveModalText('customerTypelabel')"
        :show-label="false"
        :options="productTypes.customerType"
        :model-value="financialParams.customerType"
        :disabled="disableFields"
        :display-horizontally="true"
        class="no-label"
        @change="updateParam(paramKeys.customerType, $event)"
      >
        <info-modal
          :title="retrieveInfoText('customertypeinfotitle')"
          :content="retrieveInfoText('customertypeinfotext')"
          data-qa="customer-type-info-icon"
        />
      </form-radio-button-list>
      <div v-if="isInfoIncorrect" class="error-message">
        {{ retrieveModalText('invalidcustomertype') }}
      </div>

      <div class="form-item no-info">
        <label>{{ retrieveModalText('insuredvehiclelabel') }}</label>
        <span> {{ configuration.ModelName }} {{ configuration.TrimlineName }} </span>
      </div>
      <template v-if="isGreyLicencePlate">
        <form-input
          id="cocNumber"
          v-model.lazy="financialParams[paramKeys.cocNumber]"
          :title="retrieveModalText('cocNumber')"
          type="search"
          tabindex="1"
          class="no-info"
          :disabled="disableFields"
          :tab-on-enter="true"
          @blur="searchCocNumber($event)"
        >
        </form-input>

        <form-input
          id="companyName"
          v-model.lazy="financialParams[paramKeys.companyName]"
          :title="retrieveModalText('companyName')"
          type="search"
          tabindex="1"
          class="no-info"
          :disabled="disableFields"
          @change="updateParam(paramKeys.companyName, $event)"
        >
        </form-input>

        <form-input
          id="sbiCode"
          v-model.lazy="financialParams[paramKeys.sbiCode]"
          :title="retrieveModalText('sbiCode')"
          type="search"
          tabindex="1"
          class="no-info"
          :disabled="disableFields"
          @change="updateParam(paramKeys.sbiCode, $event)"
        >
        </form-input>

        <form-textarea
          id="sbiCodeText"
          v-model.lazy="financialParams[paramKeys.sbiCodeText]"
          :title="retrieveModalText('sbiCodeName')"
          type="search"
          tabindex="1"
          class="no-info"
          :disabled="disableFields"
          @change="updateParam(paramKeys.sbiCodeText, $event)"
        >
        </form-textarea>
      </template>
      <date-picker
        id="dateOfBirth"
        :title="
          retrieveModalText(isBusinessCustomer ? 'maindriverdateofbirthlabel' : 'dateofbirthlabel')
        "
        :date="financialParams[paramKeys.dateOfBirth]"
        :min-date="minDate"
        :show-unintended-cancel-notification="false"
        :disabled="disableFieldOrLocked"
        :tab-on-enter="true"
        tabindex="1"
        @change-date="updateDateOfBirth"
      >
        <template #info>
          <info-modal
            :title="retrieveInfoText('dateofbirthinfotitle')"
            :content="retrieveInfoText('dateofbirthinfotext')"
            data-qa="date-of-birth-info-icon"
          />
        </template>
      </date-picker>

      <form-input
        id="zipCode"
        v-model.lazy="financialParams[paramKeys.zipCode]"
        :title="retrieveModalText(isBusinessCustomer ? 'businesszipcodelabel' : 'zipcodelabel')"
        type="text"
        tabindex="1"
        :disabled="disableFieldOrLocked"
        :is-read-only="disableFieldOrLocked"
        :tab-on-enter="true"
        @blur="formatZipCode($event)"
      >
        <template #info>
          <info-modal
            :title="retrieveInfoText('zipcodeinfotitle')"
            :content="retrieveInfoText('zipcodeinfotext')"
            data-qa="zip-code-info-icon"
          />
        </template>
      </form-input>

      <form-select
        id="mileage"
        class="field-select"
        tabindex="1"
        :model-value="financialParams.mileage"
        :wrapper-class="'input'"
        :options="productTypes.mileage"
        :title="retrieveModalText('mileagelabel')"
        :is-read-only="disableFieldOrLocked"
        :tab-on-enter="true"
        @change="updateParam(paramKeys.mileage, $event)"
      >
        <template #info>
          <info-modal
            :title="retrieveInfoText('mileageinfotitle')"
            :content="retrieveInfoText('mileageinfotext')"
            data-qa="mileage-info-icon"
          />
        </template>
      </form-select>

      <form-input
        id="damageFreeYears"
        v-model.lazy="financialParams[paramKeys.damageFreeYears]"
        tabindex="1"
        :title="retrieveModalText('damagefreeyearslabel')"
        type="text"
        :disabled="disableFieldOrLocked"
        :is-read-only="disableFieldOrLocked"
        :tab-on-enter="true"
        @blur="updateParam(paramKeys.damageFreeYears, $event)"
      >
        <template #info>
          <info-modal
            :title="retrieveInfoText('damagefreeyearsinfotitle')"
            :content="retrieveInfoText('damagefreeyearsinfotext')"
            data-qa="damage-free-years-info-icon"
          />
        </template>
      </form-input>

      <form-select
        id="mainCoverageType"
        class="field-select"
        tabindex="1"
        :model-value="financialParams.mainCoverageType"
        :wrapper-class="'input'"
        :options="productTypes.mainCoverageType"
        :title="retrieveModalText('mainCoverageLabel')"
        :is-read-only="disableFieldOrLocked"
        :tab-on-enter="true"
        @change="updateParam(paramKeys.mainCoverageType, $event)"
      >
        <template #info>
          <info-modal
            :title="retrieveInfoText('maincoverageinfotitle')"
            :content="retrieveInfoText('maincoverageinfotext')"
            data-qa="main-coverage-type-info-icon"
          />
        </template>
      </form-select>

      <form-select
        id="voluntaryDeductable"
        class="field-select"
        tabindex="1"
        :model-value="financialParams.voluntaryDeductable"
        :wrapper-class="'input'"
        :options="productTypes.voluntaryDeductable"
        :title="retrieveModalText('voluntarydeductablelabel')"
        :is-read-only="disableFieldOrLocked"
        :tab-on-enter="true"
        @change="updateParam(paramKeys.voluntaryDeductable, $event)"
      >
        <template #info>
          <info-modal
            :title="retrieveInfoText('voluntarydeductableinfotitle')"
            :content="retrieveInfoText('voluntarydeductableinfotext')"
            data-qa="voluntary-deductable-info-icon"
          />
        </template>
      </form-select>

      <form-checkbox
        id="extra-accessories-coverage"
        :title="
          retrieveModalText(
            isGreyLicencePlate ? 'extraaccessoriescommerciallabel' : 'extraaccessorieslabel'
          )
        "
        :disabled="disableFieldOrLocked"
        :has-info="true"
      >
        <input
          id="extra-accessories-coverage"
          v-model="extraAccessoriesCoverage"
          tabindex="1"
          name="extra-accessories-coverage"
          type="checkbox"
          :disabled="disableFieldOrLocked"
          @keydown.enter.prevent="$event.target.blur()"
          @change="updateParam(paramKeys.extraAccessories, extraAccessoriesCoverage)"
        />
        <template #info>
          <info-modal
            :title="retrieveInfoText('extraaccessoriesinfotitle')"
            :content="retrieveInfoText('extraaccessoriesinfotext')"
            data-qa="extra-accessories-info-icon"
          />
        </template>
      </form-checkbox>

      <form-select
        v-if="!isGreyLicencePlate && !newValueRegulationFixed"
        id="newValueRegulation"
        class="field-select"
        tabindex="1"
        :model-value="financialParams.newValueRegulation"
        :wrapper-class="'input'"
        :options="productTypes.newValueRegulation"
        :title="retrieveModalText('newValueRegulationlabel')"
        :is-read-only="disableFieldOrLocked"
        :tab-on-enter="true"
        @change="updateParam(paramKeys.newValueRegulation, $event)"
      >
        <template #info>
          <info-modal
            :title="retrieveInfoText('newvalueregulationinfotitle')"
            :content="retrieveInfoText('newvalueregulationinfotext')"
            data-qa="new-Value-Regulation-info-icon"
          />
        </template>
      </form-select>
      <h4>
        {{ retrieveModalText('additionalcoverage/additionalcoveragelabel') }}
      </h4>
      <!-- eslint-disable vue/no-v-for-template-key -->
      <template v-for="coverage in productTypes.additionalCoverages" :key="coverage.value">
        <form-checkbox-postfix
          :id="`coverage-${coverage.value}`"
          :title="coverage.name"
          :disabled="disableFieldOrLocked || coverage.possible === false"
          :postfix="`${
            coverage.premium ? i18n.n(coverage.premium, 'currency') : '-'
          } ${retrieveModalText('summarypostfix')}`"
        >
          <input
            :id="`coverage-${coverage.value}`"
            v-model="additionalCoveragesSelected"
            tabindex="1"
            :value="coverage.value"
            type="checkbox"
            :disabled="disableFieldOrLocked || coverage.possible === false || null"
            @keydown.enter.prevent="$event.target.blur()"
            @change="updateParam(paramKeys.additionalCoverages, additionalCoveragesSelected)"
          />
          <template #info>
            <info-modal
              :title="retrieveInfoText(`additionalcoverage/title/${coverage.value}`)"
              :content="retrieveInfoText(`additionalcoverage/text/${coverage.value}`)"
              :data-qa="`${coverage.value}-info-icon`"
            />
          </template>
        </form-checkbox-postfix>
        <form-select
          v-if="coverage.value === productTypes.additionalCoverages.ph.value"
          id="roadside-assistance-region"
          tabindex="1"
          class="field-select"
          :model-value="financialParams.roadsideAssistanceRegion"
          :wrapper-class="'input'"
          :options="productTypes.additionalCoverages.ph.specifications"
          :title="retrieveModalText('roadsideassistanceregion/label')"
          :show-label="false"
          :is-read-only="disableFieldOrLocked"
          :tab-on-enter="true"
          @change="updateParam(paramKeys.roadsideAssistanceRegion, $event)"
        >
        </form-select>
        <form-select
          v-if="isGreyLicencePlate && coverage.value === productTypes.additionalCoverages.tr.value"
          id="cargo-insurance-amount"
          tabindex="1"
          class="field-select"
          title=""
          :model-value="financialParams.cargoInsuranceAmount"
          :wrapper-class="'input'"
          :options="productTypes.additionalCoverages.tr.specifications"
          :show-label="false"
          :is-read-only="disableFieldOrLocked"
          :tab-on-enter="true"
          @change="updateParam(paramKeys.cargoInsuranceAmount, $event)"
        >
        </form-select>
      </template>
      <h4>
        {{ retrieveModalText('premiumsummary/label') }}
      </h4>
      <h5>
        {{ retrieveModalText('premiumsummary/maincoverage') }}
      </h5>
      <insurance-summary-coverage-list
        v-if="selectedMainCoverage"
        id="premium-summary-main-coverage"
        :coverages="selectedMainCoverage"
      />
      <template v-if="selectedAdditionalCoverages && selectedAdditionalCoverages.length">
        <h5>
          {{ retrieveModalText('premiumsummary/additionalcoverages') }}
        </h5>
        <insurance-summary-coverage-list
          id="premium-summary-additional-coverages"
          :coverages="selectedAdditionalCoverages"
        />
      </template>
      <horizontal-line />
      <dl class="total-line">
        <dt>{{ retrieveModalText('subtotallabel') }}</dt>
        <dl>
          <span class="total-line__value h3" data-qa="insurance-modal-price">
            {{ formattedInsurancePrice }}
          </span>
          &nbsp;{{ retrieveModalText('summarypostfix') }}
        </dl>
      </dl>
      <!-- alarm class remark; only providers that resolve one send it back -->
      <div
        v-if="['bc', 'ca'].includes(selectedMainCoverage[0]?.code) && financialParams.alarmClass"
        class="alarm-class"
      >
        {{ financialParams.alarmClass }}
      </div>
      <!-- footer -->
      <div class="disclaimer">
        <div
          data-qa="insurance-modal-disclaimer"
          v-html="retrieveModalText('disclaimertext')"
        ></div>
      </div>
      <!-- submit button -->
      <app-button type="submit" form="financing-form" data-qa="insurance-modal-submit">
        {{ retrieveModalText('confirmbutton') }}
      </app-button>
      <div v-if="isLoading" class="overlay">
        <loader />
      </div>
    </form>
  </div>
</template>

<script>
import DatePicker from '@/components/generic/presentational/atoms/datePicker/DatePicker.vue'
import FormSelect from '@/components/generic/presentational/atoms/formSelect/FormSelect.vue'
import FormInput from '@/components/generic/presentational/atoms/formInput/FormInput.vue'
import FormCheckbox from '@/components/generic/presentational/atoms/formCheckbox/FormCheckbox.vue'
import FormRadioButtonList from '@/components/generic/presentational/atoms/formRadioButtonList/FormRadioButtonList.vue'
import FormCheckboxPostfix from '@/components/generic/presentational/atoms/formCheckbox/FormCheckboxPostfix.vue'
import InfoModal from '@/components/generic/presentational/atoms/infoModal/InfoModal.vue'
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import FinancialServiceForm from '@/mixins/financialServices/financialServiceForm'
import HorizontalLine from '@/components/generic/presentational/atoms/horizontalLine/HorizontalLine.vue'
import InsuranceSummaryCoverageList from '@/components/generic/container/FinancialServices/Insurance/InsuranceSummaryCoverageList.vue'
import CurrencyHelper from '@/helpers/currencyHelper'
import { mapGetters } from 'vuex'
import FormTextarea from '@/components/generic/presentational/atoms/formTextarea/FormTextarea.vue'

export default {
  components: {
    DatePicker,
    FormInput,
    FormSelect,
    FormCheckbox,
    FormCheckboxPostfix,
    FormRadioButtonList,
    AppButton,
    Loader,
    HorizontalLine,
    InfoModal,
    InsuranceSummaryCoverageList,
    FormTextarea,
  },
  mixins: [FinancialServiceForm],
  inject: ['paramKeys', 'isBusinessCustomer', 'updateDateOfBirth', 'disableFields', 'minDate'],
  props: {
    productTypes: {
      type: Object,
      required: true,
    },
    dictionaryFolder: {
      type: String,
      required: true,
    },
    isGreyLicencePlate: {
      type: Boolean,
      required: false,
    },
    newValueRegulationFixed: {
      type: Boolean,
      required: false,
    },
    wideCoverageNames: {
      type: Boolean,
      required: false,
    },
  },
  emits: ['submit', 'updateFinancing', 'searchCocNumber'],
  data() {
    return {
      errors: null,
      additionalCoveragesSelected: [],
      extraAccessoriesCoverage: false,
    }
  },
  computed: {
    ...mapGetters({
      configuration: 'configuration/configuration',
    }),
    policyHolderDateOfBirth() {
      return new Date(this.financialParams.dateOfBirth)
    },
    formattedInsurancePrice() {
      return this.financialParams.noInsuranceAvailable || this.financialParams.errors
        ? '-'
        : CurrencyHelper.currencyRounded(this.financialParams.insurancePrice)
    },
    noInsuranceErrorMessage() {
      return this.financialParams.noInsuranceAvailable
        ? this.i18n.t('/financialservices/insurance/modal/insurance/noinsuranceerror')
        : null
    },
    errorMessage() {
      return this.financialParams.errors ? this.financialParams.errors[0] : null
    },
    selectedMainCoverage() {
      return this.productTypes.mainCoverageType.filter(
        (x) => x.value === this.financialParams.mainCoverageType
      )
    },
    selectedAdditionalCoverages() {
      return Object.entries(this.productTypes.additionalCoverages)
        .map((x) => x[1])
        .filter((x) => this.financialParams[this.paramKeys.additionalCoverages].includes(x.value))
    },
    disableFieldOrLocked() {
      return this.disableFields || this.isInfoIncorrect
    },
    isInfoIncorrect() {
      return this.isGreyLicencePlate && !this.isEigenCustomer
    },
    isEigenCustomer() {
      return this.financialParams.customerType === 'EIGEN'
    },
  },
  mounted() {
    // Set pre-selected parameters
    this.additionalCoveragesSelected = this.financialParams.additionalCoverages
      ? this.financialParams.additionalCoverages
      : this.additionalCoveragesSelected
    this.extraAccessoriesCoverage = this.financialParams.extraAccessories > 0
  },
  methods: {
    updateParam(param, value) {
      this.$emit('updateFinancing', param, value)
    },
    formatZipCode(zipCode) {
      zipCode = zipCode.toUpperCase().replace(' ', '')
      this.$emit('updateFinancing', this.paramKeys.zipCode, zipCode)
    },
    async searchCocNumber(cocNumber) {
      this.$emit('searchCocNumber', cocNumber, true)
    },
  },
}
</script>

<style lang="scss" scoped>
.financing-form {
  margin-bottom: 1rem;

  h4 {
    margin-top: 1rem;
  }

  &__info-text {
    font-style: italic;
  }

  label {
    display: block;
    font-weight: $font-weight-bold;
  }

  .disclaimer {
    img {
      float: right;
    }
  }

  :deep() {
    .field-select {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      margin: 0.5em 0;

      .input {
        flex-basis: 15rem;
        margin-right: 0;
      }
    }

    .form-item {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      margin-top: 1rem;

      input,
      textarea {
        flex-basis: 15rem;
      }

      textarea {
        height: 5rem;
      }

      .info-icon {
        margin-right: 1rem;
      }

      & > label,
      & > .label > label,
      .checkbox-item > label {
        width: 16rem;
        display: inline-block;
      }

      &.no-info {
        & > label,
        & > .label {
          flex-basis: 19rem;
        }
      }

      .postfix {
        text-align: right;
        flex-grow: 1;
      }
    }

    #customerType-container {
      .info-icon {
        margin-bottom: 0.5rem;
      }
    }

    #roadside-assistance-region-container,
    #cargo-insurance-amount-container {
      .input {
        flex-basis: 15rem;
        padding-left: 1rem;
        margin-right: 1rem;
      }
    }
  }

  // Long coverage names need a wider dropdown; the fields around it follow so they line up.
  // The roadside assistance region keeps its own width through the id rule above.
  &--wide :deep() {
    .form-item > input:not([type='checkbox']),
    .field-select .input {
      flex-basis: 17rem;
    }
  }
}

.total-line {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2rem;
  &__value {
    color: $color-primary;
    font-weight: $font-weight-bold;
  }
}

.alarm-class {
  margin-top: -1rem;
  margin-bottom: 2rem;
}

.error-message {
  color: $color-danger;
  font-size: 0.8em;
}
</style>
